import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthAgentGuardrailPolicy, GrowthAgentService } from './growth-agent.service';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-guardrails',
  imports: [
    FormsModule, AinPageComponent, InfoHintComponent, AinFormFieldComponent, TableModule, ButtonModule,
    InputTextModule, InputNumberModule, CheckboxModule, MultiSelectModule, DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.guardrails.title')" [description]="t('growth.guardrails.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.guardrails.help.title'" [descriptionKey]="'growth.guardrails.help.description'" [bulletKeys]="['growth.guardrails.help.bullets.deterministic']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.guardrails.form.title')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        <p-table [value]="items()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.guardrails.fields.policyKey') }}</th>
              <th>{{ t('growth.guardrails.fields.name') }}</th>
              <th>{{ t('growth.guardrails.fields.spendCap') }}</th>
              <th>{{ t('common.active') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.policyKey }}</td>
              <td>{{ row.name }}</td>
              <td>{{ row.spendCap ?? '-' }}</td>
              <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      <p-dialog
        [header]="t('growth.guardrails.form.title')"
        [visible]="dialogVisible()"
        (visibleChange)="dialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(720px, 96vw)' }">
        <div class="form-grid">
          <ain-form-field [label]="t('growth.guardrails.fields.policyKey')"><input pInputText [(ngModel)]="model.policyKey" /></ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.name')"><input pInputText [(ngModel)]="model.name" /></ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.agentTypes')">
            <p-multiSelect [options]="agentTypeOptions" [(ngModel)]="model.agentTypes" optionLabel="label" optionValue="value" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.spendCap')"><p-inputNumber [(ngModel)]="model.spendCap" /></ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.riskThreshold')"><p-inputNumber [(ngModel)]="model.riskThreshold" [minFractionDigits]="2" [maxFractionDigits]="2" /></ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.consentRequired')"><p-checkbox [(ngModel)]="model.consentRequired" [binary]="true" /></ain-form-field>
          <ain-form-field [label]="t('growth.guardrails.fields.requireWorkflowApproval')"><p-checkbox [(ngModel)]="model.requireWorkflowApproval" [binary]="true" /></ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; }
    .toolbar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
    .form-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:12px; }
    .dialog-footer { display:flex; justify-content:flex-end; gap:8px; flex-wrap:wrap; }
  `]
})
export class GrowthAgentGuardrailsComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  readonly items = signal<GrowthAgentGuardrailPolicy[]>([]);
  readonly dialogVisible = signal(false);
  model = { policyKey: 'default-spend', name: 'Default spend cap', agentTypes: ['Growth', 'Revenue'] as string[], spendCap: 10000 as number | null, riskThreshold: 0.75, consentRequired: true, requireWorkflowApproval: true, isActive: true };
  agentTypeOptions = ['BusinessAnalyst','IndustryAnalyst','Strategy','Growth','Revenue','Risk','Decision'].map(v => ({ label: v, value: v }));
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void { this.load(); }
  load(): void { this.service.listGuardrailPolicies().subscribe(items => this.items.set(items)); }
  openDialog(): void {
    this.model = { policyKey: 'default-spend', name: 'Default spend cap', agentTypes: ['Growth', 'Revenue'], spendCap: 10000, riskThreshold: 0.75, consentRequired: true, requireWorkflowApproval: true, isActive: true };
    this.dialogVisible.set(true);
  }
  save(): void {
    this.service.upsertGuardrailPolicy({
      policyKey: this.model.policyKey,
      name: this.model.name,
      appliesToAgentTypesJson: JSON.stringify(this.model.agentTypes),
      spendCap: this.model.spendCap,
      channelRestrictionsJson: '[]',
      consentRequired: this.model.consentRequired,
      requireWorkflowApproval: this.model.requireWorkflowApproval,
      riskThreshold: this.model.riskThreshold,
      isActive: this.model.isActive
    }).subscribe({ next: () => { this.dialogVisible.set(false); this.load(); } });
  }
}
